import {
    CashFlowConfig,
    CashFlowConfigSaveRequest,
    CashFlowConfigUpdate,
    InvestmentPolicyUpdateRequest,
} from '@ndvr/portfolio-api';

import { LifestyleDraft, toLifestylePolicy } from './withdrawals/policy-lifestyle';
import { isNewDraftId } from './withdrawals/policy-withdrawal-draft';

/** What each Investment Policy section drafts, by the key it registers under with usePolicyDraft. */
export interface PolicySections {
    withdrawalPlan: CashFlowConfig[];
    lifestyleProtection: LifestyleDraft;
}

export type PolicySectionKey = keyof PolicySections;

/** Pending drafts, each with the saved data it was started from. Only sections with changes are included. */
export type PolicyDrafts = { [K in PolicySectionKey]?: { draft: PolicySections[K]; baseline: PolicySections[K] } };

const toSaveRequest = (config: CashFlowConfig): CashFlowConfigSaveRequest => ({
    accountGUID: config.accountGUID,
    title: config.title,
    cashFlowType: config.cashFlowType,
    expenseType: config.expenseType,
    amount: config.amount,
    factorDateFrame: config.factorDateFrame,
    inflationConfig: config.inflationConfig,
});

/**
 * The backend replaces the whole plan: stored configs missing from the request are deleted, and the stored configs
 * include contributions and any withdrawals this page doesn't show. So everything the draft was not based on is
 * passed through unchanged; only withdrawals the user removed are left out.
 */
export const withdrawalPlanFromDraft = (
    draft: CashFlowConfig[],
    baseline: CashFlowConfig[],
    stored: CashFlowConfig[],
): CashFlowConfigUpdate[] => {
    const shown = new Set(baseline.map((config) => config.cashFlowConfigGUID));
    const untouched = stored.filter((config) => !shown.has(config.cashFlowConfigGUID));
    return [...draft, ...untouched].map((config) => ({
        // Drafted withdrawals carry a client-side id; the backend assigns the real one on create.
        cashFlowConfigGUID: isNewDraftId(config.cashFlowConfigGUID) ? undefined : config.cashFlowConfigGUID,
        config: toSaveRequest(config),
    }));
};

/**
 * Turns the pending section drafts into one policy update. Sections without a draft are omitted, which the backend
 * treats as "leave untouched". `storedConfigs` is only read when the withdrawal plan has a draft.
 */
export const buildPolicyUpdate = (
    { withdrawalPlan, lifestyleProtection }: PolicyDrafts,
    storedConfigs: CashFlowConfig[],
): InvestmentPolicyUpdateRequest => ({
    ...(withdrawalPlan && {
        withdrawalPlan: withdrawalPlanFromDraft(withdrawalPlan.draft, withdrawalPlan.baseline, storedConfigs),
    }),
    ...(lifestyleProtection && { lifestyleProtection: toLifestylePolicy(lifestyleProtection.draft) }),
});
